import { ApiResult, PaginatedResponse, request, safeRequest } from '@synergy/api';
import { noop } from '@synergy/utils';

import { Demand } from './models/demand';
import { DemandAccountFeedback } from './models/demand-account-feedback';
import { DemandAccountFeedbackRequest } from './containers/detail/models/feedback-request';
import { DemandAccountParticipant } from './models/demand-account-participant';
import { DemandContactTopicRegionCount } from './models/demand-contact-topic-region-count';
import { DemandNotificationContact } from './models/demand-notification-contact';
import { DemandTotalQuantityPositionsRequest } from './models/demand-total-quantity-positions-request';

export class DemandApi {
    constructor(protected baseUrl: string) {}

    public get(id: number): Promise<ApiResult<Demand>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/demands/${id}`),
            response => Demand.fromPlainObject(response.data)
        );
    }

    public post(demand: Demand): Promise<ApiResult<Demand>> {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/demands`, demand, {
                    params: { isSync: true },
                }),
            response => Demand.fromPlainObject(response.data)
        );
    }

    public patch(id: number, demand: Partial<Demand>): Promise<ApiResult<Demand>> {
        return safeRequest(
            () => request.patch(`${this.baseUrl}/demands/${id}`, demand),
            response => Demand.fromPlainObject(response.data)
        );
    }

    public start(id: number): Promise<ApiResult<void>> {
        return safeRequest(() => request.post(`${this.baseUrl}/demands/${id}/start`), noop);
    }

    public copyDemand(id: number): Promise<ApiResult<Demand>> {
        return safeRequest(
            () =>
                request.post(
                    `${this.baseUrl}/demands/${id}/copy`,
                    {},
                    {
                        params: { isSync: true },
                    }
                ),
            response => response.data
        );
    }

    public schedule(id: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/demands/${id}/schedule`),
            () => null
        );
    }

    public complete(id: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/demands/${id}/complete`),
            () => null
        );
    }

    public cget(params?: URLSearchParams): Promise<ApiResult<PaginatedResponse<Demand>>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/demands`, { params }),
            response => {
                response.data.items = response.data.items.map(Demand.fromPlainObject);
                return response.data;
            }
        );
    }

    public delete(id: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.delete(`${this.baseUrl}/demands/${id}`, { params: { isSync: true } }),
            () => null
        );
    }

    public export(params: URLSearchParams): Promise<ApiResult<Blob>> {
        const exportFields = ['number', 'status', 'title', 'responsibleContact.fullName', 'createdAt'];
        params.append('fields', exportFields.join(','));
        return safeRequest(
            () => request.get(`${this.baseUrl}/demands.csv`, { params, responseType: 'blob' }),
            response => response.data
        );
    }

    public saveTopics(id: number, topicIds: number[], regionCodes: string[]): Promise<ApiResult<void>> {
        return safeRequest(() => request.post(`${this.baseUrl}/demands/${id}/topics`, { topicIds, regionCodes }), noop);
    }

    public getTransitionData(id: number): Promise<ApiResult<Demand>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/demands/${id}/transition-data`),
            response => response.data
        );
    }

    public cgetParticipants(demandId: number): Promise<ApiResult<DemandAccountParticipant[]>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/demands/${demandId}/demand-accounts`),
            response => response.data
        );
    }

    public getFeedback(demandId: number, demandAccountId: number): Promise<ApiResult<DemandAccountFeedback>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/demands/${demandId}/demand-accounts/${demandAccountId}`),
            response => response.data
        );
    }

    public updateNotificationContacts(
        demandId: number,
        participants: DemandNotificationContact[]
    ): Promise<ApiResult<DemandAccountParticipant[]>> {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/demands/${demandId}/update-participants`, {
                    participants,
                }),
            response => response.data
        );
    }

    public updateFeedback(
        demandId: number,
        demandAccountId: number,
        feedback: DemandAccountFeedbackRequest
    ): Promise<ApiResult<DemandAccountFeedback>> {
        return safeRequest(
            () => request.patch(`${this.baseUrl}/demands/${demandId}/demand-accounts/${demandAccountId}`, feedback),
            response => response.data
        );
    }

    public decline(demandId: number, demandAccountIds: number[], reason?: string): Promise<ApiResult<void>> {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/demands/${demandId}/decline-demand-accounts`, {
                    demandAccountIds,
                    reason,
                }),
            noop
        );
    }

    public sendInfoMail(demandId: number, demandAccountId: number): Promise<ApiResult<void>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/demands/${demandId}/demand-accounts/${demandAccountId}/info-mail`),
            noop
        );
    }

    public abortSchedule(demandId: number): Promise<ApiResult<void>> {
        return safeRequest(() => request.post(`${this.baseUrl}/demands/${demandId}/abort-schedule`), noop);
    }

    public getTotalQuantityDemandPositions(id: number): Promise<ApiResult<DemandTotalQuantityPositionsRequest>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/demands/${id}/positions`),
            response => Demand.fromPlainObject(response.data)
        );
    }

    public getContactCountPerTopicAndRegion = async (
        regionCodes: string[],
        topicIds: number[]
    ): Promise<ApiResult<DemandContactTopicRegionCount[]>> => {
        const params = new URLSearchParams({ regionCodes: regionCodes.join(','), topicIds: topicIds.join(',') });

        return safeRequest(
            () => request.get(`${this.baseUrl}/topics/per-region`, { params }),
            response => response.data
        );
    };
}
